/**
 * 변수 속성 목록 관리 패널 (ID / Type / Default / Format)
 *
 * VarPropsPopup을 실제로 구동하는 화면 조각이다. 추가/편집/삭제를 모두 처리하며,
 * 어느 화면에든 한 줄로 붙일 수 있도록 자체 완결형으로 만들었다.
 *
 * - value/onChange를 주면 부모가 상태를 소유하는 제어형으로 동작하고,
 *   생략하면 내부 상태로 동작한다(비제어형).
 * - 목록은 건수가 적은 설정성 데이터라 SnGrid(sys_formgridcolumn 메타 필요) 대신
 *   일반 table로 그린다. 배포/조회 메타가 없어도 바로 쓰기 위함이다.
 */
import React, { useCallback, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import { IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { showPopupModal } from "@widgets/CommonModal";
import { showDialog } from "@widgets/CommonDialog";
import VarPropsPopup, { type VarProps } from "./VarPropsPopup";

interface Props {
  /** 제어형으로 쓸 때의 값 */
  value?: VarProps[];
  /** 값이 바뀔 때 호출 (제어형/비제어형 모두 호출된다) */
  onChange?: (next: VarProps[]) => void;
  /** 읽기 전용 — 추가/편집/삭제 버튼을 숨긴다 */
  readOnly?: boolean;
}

const TH: React.CSSProperties = {
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  padding: "6px 8px",
  borderBottom: "1px solid rgb(196 196 196)",
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  fontSize: 12,
  padding: "5px 8px",
  borderBottom: "1px solid #eee",
  wordBreak: "break-all",
};

/** 빈 값은 표에서 구분이 안 되므로 대시로 표시 */
const show = (v: string) => (v && v.trim() ? v : "-");

/**
 * jsonb 컬럼(js_param)에 담긴 변수 속성 목록을 배열로 되돌린다.
 *
 * 같은 값이 경로에 따라 네 가지 모양으로 내려온다. 넷 다 받아야 목록이 뜬다.
 *   1) 배열           [{...}]                     — 매퍼가 파싱해 준 경우
 *   2) JSON 문자열     '[{...}]'                   — 문자열로 그대로 온 경우
 *   3) 객체 래퍼       {type:'jsonb', value:'[…]'} — 드라이버가 감싼 경우
 *   4) 이중 인코딩     '"[{...}]"'                 — jsonb에 JSON 스칼라로 저장된 경우
 *
 * 예전에는 1·2만 처리해서 3·4는 조용히 빈 배열이 됐다(에러도 안 나서 원인 찾기가 어렵다).
 * 값이 1건짜리 스칼라 등 배열이 아닌 것은 지금도 빈 배열로 흘린다.
 */
export const parseVarProps = (raw: any): VarProps[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;

  // 3) 드라이버가 감싼 객체 — 안쪽 문자열을 다시 태운다
  if (typeof raw === "object") {
    const inner = (raw as any).value;
    return typeof inner === "string" ? parseVarProps(inner) : [];
  }
  if (typeof raw !== "string") return [];

  // 2)·4) 배열이 나올 때까지 몇 겹만 벗겨본다
  let value: any = raw;
  for (let i = 0; i < 3; i++) {
    try {
      value = JSON.parse(typeof value === "string" ? value.trim() : value);
    } catch {
      return [];
    }
    if (Array.isArray(value)) return value;
    if (typeof value !== "string") return [];
  }
  return [];
};

export default function VarPropsPanel({ value, onChange, readOnly }: Props) {
  const [inner, setInner] = useState<VarProps[]>([]);
  // value가 오면 제어형 — 부모 값이 항상 우선한다
  const rows = value ?? inner;

  const commit = useCallback(
    (next: VarProps[]) => {
      if (value === undefined) setInner(next);
      onChange?.(next);
    },
    [value, onChange]
  );

  // 같은 ID가 두 개면 어느 쪽이 쓰이는지 알 수 없으므로 막는다.
  // 편집 중인 행(skipIndex)은 자기 자신과 비교하지 않는다.
  const isDuplicated = useCallback(
    (id: string, skipIndex?: number) =>
      rows.some(
        (r, i) =>
          i !== skipIndex && r.id.trim().toLowerCase() === id.trim().toLowerCase()
      ),
    [rows]
  );

  const openPopup = useCallback(
    async (defaultValue?: VarProps) => {
      const result = await showPopupModal({
        title: defaultValue ? "변수 속성 수정" : "변수 속성 추가",
        children: <VarPropsPopup defaultValue={defaultValue} />,
        width: "420px",
        height: "auto",
        validation: true,
      });
      // 취소/X — showPopupModal은 undefined 또는 "close"를 준다
      if (!result || result === "close" || result === "ok") return null;
      // 팝업은 SinglePopup과 같은 규약으로 행 1건을 배열에 담아 돌려준다
      const row = Array.isArray(result) ? result[0] : result;
      return (row ?? null) as VarProps | null;
    },
    []
  );

  const onAdd = async () => {
    const added = await openPopup();
    if (!added) return;
    if (isDuplicated(added.id)) {
      await showDialog("alter", `이미 있는 ID입니다. (${added.id})`);
      return;
    }
    commit([...rows, added]);
  };

  const onEdit = async (index: number) => {
    const edited = await openPopup(rows[index]);
    if (!edited) return;
    if (isDuplicated(edited.id, index)) {
      await showDialog("alter", `이미 있는 ID입니다. (${edited.id})`);
      return;
    }
    commit(rows.map((r, i) => (i === index ? edited : r)));
  };

  const onDelete = async (index: number) => {
    const target = rows[index];
    const ok = await showDialog("confirm", `'${target.id}' 변수를 삭제할까요?`);
    if (!ok) return;
    commit(rows.filter((_, i) => i !== index));
  };

  const isEmpty = useMemo(() => rows.length === 0, [rows]);

  return (
    <div className="w-full">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700 }}>
          변수 속성 {rows.length > 0 && `(${rows.length})`}
        </span>
        {!readOnly && (
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAdd}
          >
            추가
          </Button>
        )}
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={TH}>ID</th>
            <th style={TH}>Type</th>
            <th style={TH}>Default</th>
            <th style={TH}>Format</th>
            {!readOnly && <th style={{ ...TH, width: 72 }}></th>}
          </tr>
        </thead>
        <tbody>
          {isEmpty && (
            <tr>
              <td
                colSpan={readOnly ? 4 : 5}
                style={{ ...TD, textAlign: "center", color: "#888", padding: "16px 8px" }}
              >
                등록된 변수가 없습니다.
              </td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr
              key={`${r.id}-${i}`}
              onDoubleClick={() => !readOnly && onEdit(i)}
              style={{ cursor: readOnly ? "default" : "pointer" }}
            >
              <td style={{ ...TD, fontWeight: 600 }}>{show(r.id)}</td>
              <td style={TD}>{show(r.type)}</td>
              <td style={TD}>{show(r.default)}</td>
              <td style={TD}>{show(r.format)}</td>
              {!readOnly && (
                <td style={{ ...TD, whiteSpace: "nowrap" }}>
                  <Tooltip title="수정">
                    <IconButton size="small" onClick={() => onEdit(i)}>
                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="삭제">
                    <IconButton size="small" onClick={() => onDelete(i)}>
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
